import React, { useState, useCallback, useEffect } from "react";

import { makeStyles } from "@material-ui/core/styles";
import {
  Box,
  Container,
  Grid,
  Card,
  CardContent,
  CardHeader,
  Button,
  Divider,
} from "@material-ui/core";

import HeaderClientes from "components/_refactor/headers/clients";
import useSnackbar from "components/Alerts/Snackbar/useSnackbar";
import StructureFields from "components/_refactor/fields/structure";
import useForm from "hooks/form";
import componentStyles from "assets/theme/views/admin/react-bs-table";
import PeriodField, {
  getPeriodLabel,
} from "components/_refactor/fields/period-field";
import TableRecebimentoNFs from "./table/TableRecebimentoNFs";
import ClearButton from "components/_refactor/button/clear";
import LoadingData from "components/Loading/LoadingData";
import moment from "moment";
import { DialogRegistroDeNota } from "components/Dialogs/DialogRegistroDeNota";
import useLogs from "hooks/logs";
import { useVisionLogin } from "hooks/useVisionLogin";
import { useWebsocketFetch } from "hooks/useWebsocketFetch";
import { API_ENV } from "constants/environment";
import AutoCompleteField from "components/_refactor/fields/autocomplete";
import { IsMobile900, IsMobile } from "utils/is-mobile";
import InputText from "components/_refactor/text/input";

const useStyles = makeStyles(componentStyles);
const useCustomStyles = makeStyles({
  fields: {
    display: "flex",
    flexDirection: "column",
    width: 300,
    marginTop: "1rem",
  },
  option: {
    whiteSpace: "nowrap",
    textOverflow: "ellipsis",
    wordBreak: "keep-all",
  },
  paper: {
    width: "max-content !important",
    minWidth: "100%",
  },
  popper: {
    maxWidth: "calc(100% - 60px - 1.5rem)",
    width: "max-content",
  },
});

const dataHoje = new Date().toISOString().slice(0, 10);
const data30DiasAtras = moment(dataHoje)
  .subtract(30, "days")
  .format("YYYY-MM-DD");

const situacaoOptions = [
  { value: "comentradas", name: "Com entradas em loja" },
  { value: "problemaentrega", name: "Com problema de entrega" },
  { value: "sementrada", name: "Sem entradas em loja" },
  { value: "todos", name: "Todos" },
];

const ordenacaoOptions = [
  { value: 2, name: "Data Emissão NF descendente" },
  { value: 3, name: "Data Última Ocorrência descendente" },
  { value: 1, name: "Nome Loja ascendente" },
  { value: 4, name: "Numero NF descendente" },
];

function RelatorioRecebimentoNFs() {
  const snackbar = useSnackbar();
  const { setLocalStorageActions } = useLogs();
  const classes = { ...useStyles(), ...useCustomStyles() };
  const { setLocalStorageApiProblem } = useLogs();

  const { userId } = useVisionLogin();

  const [structureFilters, setStructureFilters] = React.useState({});
  const [structureLoading, setStructureLoading] = React.useState([]);

  const [tableTitle, setTableTitle] = useState("");
  const [clearFilters, setClearFilters] = useState(false);
  const [cancelLoading, setCancelLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [notasFiscais, setNotasFiscais] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [currentSizePerPage, setCurrentSizePerPage] = useState(25);

  const [hasSearched, setHasSearched] = useState(false);

  const isMobile900 = IsMobile900();
  const isMobile = IsMobile();

  const itemXS = isMobile ? 12 : isMobile900 ? 6 : 3;

  const { values, handleChange, errors } = useForm({
    numeroNf: "",
    periodoEmissao: null,
    dataInicialEmissao: data30DiasAtras,
    dataFinalEmissao: dataHoje,
    situacao: "todos",
    ordenacao: 2,
  });

  const {
    handleFetchData,
    status,
    handleFetchUrl,
    cancel: cancelWebsocket,
  } = useWebsocketFetch({
    onSuccess: (data) => {
      console.log("DATA", data);
      if (values.periodoEmissao) {
        setTableTitle(
          `Recebimento de NFs ${getPeriodLabel({
            period: values.periodoEmissao?.value,
            initialDate: values.dataInicialEmissao,
            finalDate: values.dataFinalEmissao,
          })}`,
        );
      } else {
        setTableTitle(`Recebimento de NFs`);
      }
      setNotasFiscais(data);
    },
    onError: (error) => {
      setErrorMessage(error);
      setLocalStorageApiProblem({
        idMenuAccess: "110",
        screen: "Relatório Recebimento de NFs",
        problem: `Erro na chamada do endpoint list-reception-invoices - ${error}`,
      });
    },
  });

  const searching = status === "fetching";

  const toggleFormDialog = useCallback((formType) => {
    if (formType) {
      setOpenDialog(formType);
    }
  }, []);

  const searchTitles = useCallback(async () => {
    setHasSearched(true);
    setCancelLoading(false);
    setErrorMessage("");

    const initialFormat = moment(values.dataInicialEmissao).format(
      "YYYY-MM-DD",
    );
    const finalFormat = moment(values.dataFinalEmissao).format("YYYY-MM-DD");

    handleFetchData({
      action: "list-reception-invoices",
      params: {
        querystring: {
          idMarca: structureFilters?.idMarca || "TODAS",
          idRepresentante: structureFilters?.idRepresentante,
          idGrupoEconomico: structureFilters?.idGrupoEconomico,
          idLoja: structureFilters?.idLoja || "TODAS",
          dtIni: initialFormat,
          dtFim: finalFormat,
          numeroNf: values?.numeroNf,
          situacao: values?.situacao,
          ordenacao: values?.ordenacao?.toString(),
          retornarItens: true,
          retornarOcorrencias: false,
        },
        header: {
          "K-IdUser": userId,
        },
      },
    });
  }, [
    structureFilters,
    values.dataFinalEmissao,
    values.dataInicialEmissao,
    values.numeroNf,
    values.ordenacao,
    values.periodoEmissao,
    values.situacao,
  ]);

  const handleClearFilters = () => {
    setClearFilters(true);
    setStructureLoading(true);
    handleChange({
      target: {
        name: "situacao",
        value: "todos",
      },
    });
    handleChange({
      target: {
        name: "numeroNf",
        value: "",
      },
    });
    handleChange({
      target: {
        name: "periodoEmissao",
        value: null,
      },
    });
    handleChange({
      target: {
        name: "ordenacao",
        value: 2,
      },
    });
    handleChange({
      target: {
        name: "dataInicialEmissao",
        value: data30DiasAtras,
      },
    });
    handleChange({
      target: {
        name: "dataFinalEmissao",
        value: dataHoje,
      },
    });
    setStructureLoading(false);
  };

  const handleStopSearching = () => {
    setCancelLoading(true);
    setTableTitle("");
    cancelWebsocket();
  };

  const handleVerifyRequiredInputs = () => {
    setLocalStorageActions({
      nomeTela: "Relatório de Recebimento de NFs",
      idMenu: "110",
      acao: "Clicou para Pesquisar",
    });
    return searchTitles();
  };

  useEffect(() => {
    if (errorMessage !== "" && !cancelLoading) {
      snackbar.showMessage(errorMessage, {
        severity: "error",
      });
    }
  }, [cancelLoading, errorMessage]);

  useEffect(() => {
    if (
      (structureFilters?.idMarca === null &&
        structureFilters?.idCanal === "" &&
        structureFilters?.idRepresentante === "" &&
        structureFilters?.idGrupoEconomico === "") ||
      values.numeroNf === ""
    ) {
      setClearFilters(false);
    }
  }, [structureFilters, values]);

  useEffect(() => {
    handleFetchUrl(
      `wss://3zvsu3yj2b.execute-api.us-east-1.amazonaws.com/${API_ENV}`,
    );
  }, []);

  useEffect(() => {
    if (status === "empty") {
      snackbar.showMessage("Sua busca não retornou nenhum resultado.", {
        severity: "success",
      });
      setNotasFiscais([]);
    }
  }, [status]);

  return (
    <>
      <HeaderClientes
        section="Relatório de Recebimento de NFs"
        subsection="Estoque"
      />
      <Container style={{ marginTop: "-4.5rem" }} maxWidth={false}>
        <Grid container>
          <Grid item xs={12}>
            <Card classes={{ root: classes.cardRoot }}>
              <CardHeader
                className={classes.cardHeader}
                title="Filtros"
                titleTypographyProps={{
                  component: Box,
                  marginBottom: "0!important",
                  variant: "h3",
                }}
              ></CardHeader>
              <CardContent>
                <Grid container spacing={2} alignItems="center">
                  <StructureFields
                    clearFilters={clearFilters}
                    onStructureFieldsChange={setStructureFilters}
                    disableAll={searching}
                    onStructureFieldsLoad={setStructureLoading}
                    requiredFields={["brand"]}
                    hideFields={["channel"]}
                    fieldsOrder={["economicGroup", "store"]}
                  />
                  <Grid item xs={12}>
                    <Divider style={{ margin: "0.5rem 0" }} />
                  </Grid>

                  <Grid item xs={itemXS}>
                    <AutoCompleteField
                      label="Situação"
                      required
                      options={situacaoOptions}
                      optionLabelKey="name"
                      optionIdKey="value"
                      value={
                        situacaoOptions.find(
                          (item) => item.value === values.situacao,
                        ) || null
                      }
                      disabled={searching}
                      onChange={(newValue) => {
                        handleChange({
                          target: {
                            name: "situacao",
                            value: newValue ? newValue.value : "",
                          },
                        });
                      }}
                    />
                  </Grid>

                  <Grid item xs={itemXS}>
                    <InputText
                      label="Número da NF"
                      type="number"
                      value={values.numeroNf}
                      onChange={(e) => {
                        handleChange({
                          target: {
                            name: "numeroNf",
                            value: e.target.value.slice(0, 50),
                          },
                        });
                      }}
                      disable={searching}
                    />
                  </Grid>

                  <Grid
                    item
                    xs={
                      values.periodoEmissao?.value === "Outros" ? "12" : itemXS
                    }
                  >
                    <PeriodField
                      label="Data de Emissão"
                      value={values.periodoEmissao}
                      onChange={(option) =>
                        handleChange({
                          target: {
                            name: "periodoEmissao",
                            value: option,
                          },
                        })
                      }
                      initialDate={values.dataInicialEmissao}
                      finalDate={values.dataFinalEmissao}
                      setInitialDate={(value) =>
                        handleChange({
                          target: {
                            name: "dataInicialEmissao",
                            value,
                          },
                        })
                      }
                      setFinalDate={(value) =>
                        handleChange({
                          target: {
                            name: "dataFinalEmissao",
                            value,
                          },
                        })
                      }
                      disabled={searching}
                    />
                  </Grid>

                  <Grid item xs={itemXS}>
                    <AutoCompleteField
                      label="Ordenar por"
                      options={ordenacaoOptions}
                      optionLabelKey="name"
                      optionIdKey="value"
                      value={
                        ordenacaoOptions.find(
                          (item) => item.value === values.ordenacao,
                        ) || null
                      }
                      disabled={searching}
                      onChange={(newValue) => {
                        handleChange({
                          target: {
                            name: "ordenacao",
                            value: newValue ? newValue.value : "",
                          },
                        });
                      }}
                    />
                  </Grid>
                </Grid>
                <Grid container spacing={2}>
                  <Grid item xs={isMobile ? 12 : isMobile900 && 6}>
                    <Button
                      style={
                        isMobile || isMobile900
                          ? {
                              width: "100%",
                              marginBottom: "0.75rem",
                              marginTop: "0.75rem",
                            }
                          : { marginBottom: "0rem" }
                      }
                      variant="contained"
                      onClick={handleVerifyRequiredInputs}
                      disabled={
                        searching ||
                        Object.keys(errors).length > 0 ||
                        structureLoading.length > 0
                      }
                    >
                      {searching ? "Pesquisando..." : "Pesquisar"}
                    </Button>
                  </Grid>
                  <Grid
                    item
                    xs={isMobile ? 12 : isMobile900 && 6}
                    style={{ display: "flex", alignItems: "center" }}
                  >
                    <ClearButton
                      disable={searching || structureLoading.length > 0}
                      onClick={() => handleClearFilters()}
                      fullWidth={true}
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
            <LoadingData
              searching={searching}
              onClick={() => handleStopSearching()}
              classes={classes.root}
              label="Buscando dados..."
            />
          </Grid>
        </Grid>
        {!searching && notasFiscais && !cancelLoading ? (
          <TableRecebimentoNFs
            isMobile={isMobile}
            dataTable={notasFiscais}
            searchTitles={searchTitles}
            tableTitle={tableTitle}
            toggleFormDialog={toggleFormDialog}
            setSelectedRow={setSelectedRow}
            hasSearched={hasSearched}
            currentPage={currentPage}
            currentSizePerPage={currentSizePerPage}
            handlePageChange={(page) => setCurrentPage(page)}
            handleSizePerPageChange={(sizePerPage) =>
              setCurrentSizePerPage(sizePerPage)
            }
          />
        ) : null}
      </Container>
      {Boolean(openDialog) && (
        <DialogRegistroDeNota
          open
          formType={openDialog}
          onClose={({ type }) => {
            setSelectedRow(null);
            setOpenDialog(null);
            if (type === "creating") {
              searchTitles();
            }
          }}
          selectedReason={selectedRow}
        />
      )}
    </>
  );
}

export default RelatorioRecebimentoNFs;
